// src/com/user_alloc/UserAllocInput.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import {
    Button, DialogContent, DialogActions, Grid, CircularProgress, Popover, Divider
} from '@mui/material';
import { AppWord, AppObject, AppStyle, InputType, UserClass } from '../../value/AppConst';
import PhoneField from '../../common/PhoneField';
import DaumAddressSearch from '../../common/DaumAddressSearch';
import VzmTextField from '../../common/VzmTextField';
import VzmCheckbox from '../../common/VzmCheckbox';
import VzmSelector from '../../common/VzmSelector';
import { ErrorHintBox } from '../../common/VzmStyled';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import InputUtil from '../../value/InputUtil';
import { useAlertSetter } from '../../api/app-hook';
import {
    authClassSimpleListRepo, comInfoSimpleListRepo, dcInfoSimpleListRepo, storeInfoSimpleListRepo
} from '../../value/AppModel';
import IsValid from '../../value/IsValid';
import { CHECK_USER_ID_UNSAFE } from '../user_info/UserInfoGql';
import AutoTextField from '../../common/AutoTextField';
import InformForgotPassword from '../user_info/InformForgotPassword';

const IdMessage = styled(Box)({
    fontSize: '0.8em', color: '#338',
});

const InputMap = {
    // toAdd, toEdit : mark if need to submit for mutation
    userId : {
        id: 'userId', label: '사용자ID',
        type: InputType.text, required: true, toAdd: true, toEdit: true,
        help: '25자 이내로 입력하세요.',
        check: (value) => {return IsValid.string(value, 0, 25)},
    },
    userName : {
        id: 'userName', label: '이름',
        type: InputType.text, required: true, toAdd: true, toEdit: true,
        help: '128자 이내로 입력하세요.',
        check: (value) => {return IsValid.string(value, 0, 128)},
    },
    password : {
        id: 'password', label: '암호',
        type: InputType.text, required: true, toAdd: true, toEdit: true,
        help: '대문자,소문자,숫자,기호 중 3가지 이상 섞어 8자 이상(개인정보보호법).',
        check: (value) => {return IsValid.passwordStrict(value)},
    },
    passwordConfirm: {
        id: 'passwordConfirm', label: '암호확인',
        type: InputType.text, required: false, toAdd: false, toEdit: true,
        help: '암호와 동일하게 입력하세요.',
    },
    zipCd : {
        id: 'zipCd', label: '우편번호',
        type: InputType.text, required: false, toAdd: true, toEdit: true,
        check: (value) => {return IsValid.string(value, 0, 7)},
    },
    addr : {
        id: 'addr', label: '주소',
        type: InputType.text, required: false, toAdd: true, toEdit: true,
        check: (value) => {return IsValid.string(value, 0, 120)},
    },
    telno : {
        id: 'telno', label: '연락처',
        type: InputType.text, required: false, toAdd: true, toEdit: true,
        check: (value) => {return IsValid.string(value, 0, 15)},
    },
    /* mobile : {
        id: 'mobile', label: 'mobile',
        type: InputType.text, required: false, toAdd: true, toEdit: true,
        help: '64자 이내로 입력하세요.',
        check: (value) => {return IsValid.string(value, 0, 64)},
    }, */
    email : {
        id: 'email', label: '메일주소',
        type: InputType.email, required: false, toAdd: true, toEdit: true,
        help: '형식에 맞도록 메일주소를 입력하세요.',
        check: (value) => {return IsValid.nullString(value) || IsValid.email(value)},
    },
    authClassId : {
        id: 'authClassId', label: '권한',
        labelKey: 'className',
        type: InputType.select, required: true, toAdd: true, toEdit: true,
        default: UserClass.store.id.toString(),
    },
    dcCd : {
        id: 'dcCd', label: '소속DC',
        labelKey: 'dcName',
        type: InputType.text, required: false, toAdd: true, toEdit: true,
    },
    storeId : {
        id: 'storeId', label: '소속점포',
        labelKey: 'storeName',
        type: InputType.text, required: false, toAdd: true, toEdit: true,
    },
    comId : {
        id: 'comId', label: '소속운송사',
        labelKey: 'comName',
        type: InputType.text, required: false, toAdd: true, toEdit: true,
    },
    allDc : {
        id: 'allDc', label: 'DC상태전환 가능',
        type: InputType.checkbox, required: false, toAdd: true, toEdit: true,
        default: false,
    },
    /* note : {
        id: 'note', label: '거절사유',
        type: InputType.text, required: false, toAdd: false, toEdit: false,
    }, */
};

// const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);

/**
 * (USER_ALLOC) 정보 생성 또는 편집 Dialog의 내용 부분
 * 사용자 승인요청
 * 로그인 화면(AppLogin.js) 아래에 있는 버튼을 누르면 활성화 되는 화면 내에 들어감.
 * 신규요청, 변경요청을 위한 서브 컴포넌트
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2025-09-19 13:39:47 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function UserAllocInput({
    RequestTabs,
    onClose,
    isEdit,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const authClassList = useReactiveVar(authClassSimpleListRepo);
    const dcInfoList = useReactiveVar(dcInfoSimpleListRepo);
    const storeInfoList = useReactiveVar(storeInfoSimpleListRepo);
    const comInfoList = useReactiveVar(comInfoSimpleListRepo);
    const [userExists, setUserExists] = useState(false);
    const [userIdChecked, setUserIdChecked] = useState(false);
    const [messageOnChecking, setMessageOnChecking] = useState('');
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [errorLabels, setErrorLabels] = useState([]);
    const [moreError, setMoreError] = useState(false);
    const [anchorAddr, setAnchorAddr] = useState(null);
    const addAlert = useAlertSetter();
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.
    const [checkUserId, responseCheckUserId] = useLazyQuery(CHECK_USER_ID_UNSAFE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteCheckUserId(data, option)},
        onError: (error) => addAlert({title: "아이디 확인 오류", message: [error.message]})
    });

    if(isEdit) {
        InputMap.password.check = (value) => {return IsValid.string(value, 8, 50)};
    }
    else {
        InputMap.password.check = (value) => {return IsValid.passwordStrict(value)};
    }

    useEffect(() => {
        const labels = [];
        if(input.errorLabels && input.errorLabels.length > 0) {
            labels.push(...input.errorLabels);
        }
        if(input.data.authClassId) {
            let errorMore = false;
            if(input.data.authClassId === UserClass.transCompany.id.toString() || input.data.authClassId === UserClass.transCompanyDriver.id.toString()) {
                if(IsValid.nullString(input.data.comId)) {
                    labels.push(InputMap.comId.label);
                    errorMore = true;
                }
            }
            else if(input.data.authClassId === UserClass.store.id.toString()) {
                if(IsValid.nullString(input.data.storeId)) {
                    labels.push(InputMap.storeId.label);
                    errorMore = true;
                }
            }
            else {
                if(IsValid.nullString(input.data.dcCd)) {
                    labels.push(InputMap.dcCd.label);
                    errorMore = true;
                }
            }
            setMoreError(errorMore);
        }
        setErrorLabels(labels);
    }, [input]);

    useEffect(() => {
        if(isEdit) {
            if(userIdChecked) {
                if(userExists) setMessageOnChecking('사용자 ID가 확인되었습니다.');
                else setMessageOnChecking('등록되지 않은 사용자 ID입니다.');
            }
            else {
                setMessageOnChecking('사용자 ID 등록상태를 확인하세요.');
            }
        }
        else {
            if(userIdChecked) {
                if(userExists) setMessageOnChecking('이미 등록된 사용자 ID입니다. 다른 ID를 사용하거나 관리자에게 문의하세요.');
                else setMessageOnChecking('사용 가능한 ID입니다.');
            }
            else {
                setMessageOnChecking('사용자 ID를 입력한 후 반드시 중복상태를 확인하세요.');
            }
        }
    }, [isEdit, userIdChecked, userExists]);

    // const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);

    const onCompleteCheckUserId = (data, clientOption) => {
        let exists = false;
        setUserIdChecked(true);
        if(data.userInfoCheckUserIdUnsafe) {
            if(data.userInfoCheckUserIdUnsafe.userNid > 0) {
                exists = true;
            }
        }
        setUserExists(exists);
    };

    const onChangeValue = (id, value, error) => {
        let error2 = error;
        if(!isEdit) {
            if(!error && id===InputMap.passwordConfirm.id) {
                error2 = value !== input.data.password;
            }
        }
        const newInput = InputUtil.reflectData(input, id, value, error2, InputMap, isEdit);
        if(id === InputMap.authClassId.id) {
            if(
                value === UserClass.transCompany.id.toString()
                || value === UserClass.transCompanyDriver.id.toString()
                || value === UserClass.store.id.toString()
            ) {
                newInput.data.allDc = false;
            }
        }
        setInput(newInput);
        if(id === InputMap.userId.id) {
            setUserIdChecked(false);
        }
    };

    const onSelectAddress = (addr) => {
        if(addr) {
            const newInputOne = InputUtil.reflectData(input, InputMap.zipCd.id, addr.zipCd, !InputMap.zipCd.check(addr.zipCd), InputMap, isEdit);
            const newInput = InputUtil.reflectData(newInputOne, InputMap.addr.id, addr.addr, !InputMap.addr.check(addr.addr), InputMap, isEdit);
            setInput(newInput);
        }
        setAnchorAddr(null);
    };
        
    const onStoreSelected = (id, value) => {
        if(value !== AppWord.ALL) onChangeValue(id, value, false);
    };

    const onChangeStoreInputText = (e, value, reason) => {
        if(reason === 'clear' || (reason === 'input' && value === '')) {
            onChangeValue(InputMap.storeId.id, '', false);
        }
    };

    const onCompanySelected = (id, value) => {
        if(value !== AppWord.ALL) onChangeValue(id, value, false);
    };

    const onChangeCompanyInputText = (e, value, reason) => {
        if(reason === 'clear' || (reason === 'input' && value === '')) {
            onChangeValue(InputMap.comId.id, '', false);
        }
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        const newParam = {
            userId: param.userId,
            userName: param.userName,
            password: param.password,
            authClassId: param.authClassId,
            zipCd: param.zipCd,
            addr: param.addr,
            telno: param.telno,
            email: param.email,
            allDc: param.allDc,
        };
        if(param.authClassId === UserClass.transCompany.id.toString()
            || param.authClassId === UserClass.transCompanyDriver.id.toString()
        ) {
            newParam.comId = param.comId;
        }
        else if(param.authClassId === UserClass.store.id.toString()) {
            newParam.storeId = param.storeId;
        }
        else {
            newParam.dcCd = param.dcCd;
        }
        onRequestSave(newParam, isEdit);
    };

    const askCancel = async () => {
        const confirmed= window.confirm("변경된 내용이 있습니다. 저장하지 않고 닫으시겠습니까?");
        if(confirmed) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    const onClickCheckExistUserId = () => {
        if(input.data.userId && input.data.userId.length > 0) {
            checkUserId({variables:{userId: input.data.userId}});
        }
    };

    // Input field initiallized using getInputField(c,t,i)
    return(
        <>
            <DialogContent>
                {RequestTabs}
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <VzmTextField
                            dict={InputMap.userId}
                            value={input.data.userId}
                            onChange={onChangeValue}
                        />
                        {
                            isEdit ?
                            <Button
                                onClick={onClickCheckExistUserId}
                                variant='outlined'
                                style={{marginLeft:10}}
                                disabled={
                                    !input.data.userId || input.data.userId.length === 0
                                    || userIdChecked
                                    || responseCheckUserId.loading
                                }
                            >ID 확인</Button>
                            :
                            <Button
                                onClick={onClickCheckExistUserId}
                                variant='outlined'
                                style={{marginLeft:10}}
                                disabled={
                                    !input.data.userId || input.data.userId.length === 0
                                    || userIdChecked
                                    || responseCheckUserId.loading
                                }
                            >중복확인</Button>
                        }
                    </Grid>
                    <Grid item xs={3}>
                        <VzmTextField
                            dict={InputMap.password}
                            value={input.data.password}
                            onChange={onChangeValue}
                            fullWidth={true}
                            isPassword={true}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        {
                            isEdit ? 
                            <span style={{
                                    fontSize: '0.8em', color: '#f88',
                                    marginTop: 10, display: 'inline-block'
                                }}
                            >
                                본인 확인을 위해 암호를 입력하세요.
                            </span> :
                            <VzmTextField
                                dict={InputMap.passwordConfirm}
                                value={input.data.passwordConfirm}
                                onChange={onChangeValue}
                                fullWidth={true}
                                isPassword={true}
                            />}
                    </Grid>
                    <Grid item xs={6}>
                        <IdMessage>
                        {messageOnChecking}
                        </IdMessage>
                    </Grid>
                    <Grid item xs={6}>
                        <IdMessage>
                        {
                            isEdit ?
                            <InformForgotPassword />
                            :
                            <span>
                                암호는 대문자, 소문자, 숫자, 특수문자 중 3가지 이상을 섞어<br/>
                                8자 이상으로 설정하세요. (개인정보보호법)
                            </span>
                        }
                        </IdMessage>
                    </Grid>
                    <Grid item xs={3}>
                        <VzmSelector
                            dict={InputMap.authClassId}
                            value={input.data.authClassId}
                            onChange={onChangeValue}
                            list={authClassList.sort((a,b) => a.authClassId - b.authClassId)}
                        />
                    </Grid>
                    <Grid item xs={9}>
                        {
                            input.data.authClassId <= 2 &&
                            <span style={{fontSize: '0.75em', color: '#33f'}}>
                                관리자, 마스터 권한 신청은 관리자에게 문의 후 신청 바랍니다.<br/>
                                e_hclee916@homeplus.co.kr / (031) 646-7226 / 이희청
                            </span>
                        }
                    </Grid>
                    <Grid item xs={4}>
                        {
                            input.data.authClassId === UserClass.transCompany.id.toString()
                            || input.data.authClassId === UserClass.transCompanyDriver.id.toString()
                            ?
                            <AutoTextField
                                dict={InputMap.comId}
                                value={input.data.comId || ''}
                                onChange={onCompanySelected}
                                onInputChange={onChangeCompanyInputText}
                                list={comInfoList}
                                style={{minWidth:250, marginRight: 10}}
                                listBoxProps={{style:{maxHeight: 200, overflowY: 'auto'}}}
                            />
                            :
                            input.data.authClassId === UserClass.store.id.toString()
                            ?
                            <AutoTextField
                                dict={InputMap.storeId}
                                value={input.data.storeId || ''}
                                onChange={onStoreSelected}
                                onInputChange={onChangeStoreInputText}
                                list={storeInfoList}
                                style={{minWidth:250, marginRight: 10}}
                                listBoxProps={{style:{maxHeight: 200, overflowY: 'auto'}}}
                            />
                            :
                            <VzmSelector
                                dict={InputMap.dcCd}
                                value={input.data.dcCd}
                                onChange={onChangeValue}
                                list={dcInfoList}
                                fullWidth={true}
                            />}
                    </Grid>
                    <Grid item xs={8}> </Grid>
                    <Grid item xs={12}>
                        <VzmCheckbox
                            dict={InputMap.allDc}
                            value={input.data.allDc}
                            onChange={onChangeValue}
                            fullWidth={true}
                            disabled={
                                input.data.authClassId === UserClass.store.id.toString()
                                || input.data.authClassId === UserClass.transCompany.id.toString()
                                || input.data.authClassId === UserClass.transCompanyDriver.id.toString()
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <VzmTextField
                            dict={InputMap.userName}
                            value={input.data.userName}
                            onChange={onChangeValue}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <PhoneField
                            dict={InputMap.telno}
                            value={input.data.telno}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <VzmTextField
                            dict={InputMap.email}
                            value={input.data.email}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <VzmTextField
                            dict={InputMap.zipCd}
                            value={input.data.zipCd}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <VzmTextField
                            dict={InputMap.addr}
                            value={input.data.addr}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <Button
                            size={AppWord.LARGE}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                            onClick={(e)=>{setAnchorAddr(e.currentTarget);}}
                        >
                            주소찾기
                        </Button>
                        <Popover
                            anchorEl={anchorAddr}
                            open={Boolean(anchorAddr)}
                            onClose={()=>{setAnchorAddr(null);}}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right', }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right', }}
                        >
                            <DaumAddressSearch open={Boolean(anchorAddr)} onSelect={onSelectAddress}/>
                        </Popover>
                    </Grid>
                
                </Grid>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={
                                input.hasError || !input.changed
                                || !userIdChecked
                                || moreError
                            }
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </>
    );
}
